import dayjs from 'dayjs'
import { useCallback, useMemo } from 'react'
import {
  CartesianGrid,
  Line,
  LabelList,
  LabelProps,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts'
import {
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent'
import {
  ChartContainer,
  ChartTitle,
  ToolTipContainer,
  TooltipInnerContainer,
} from '../../containers/PatientsView/PatientTabs/RecordedEcgTab/RecordedEcgTabStyle'
import { PatientRecordedDataType } from '../../state/recordedData/types'

type RecordedDataLineChartProp = {
  lineChartDataKey?: 'spo2' | 'pr' | 'resp' | 'temp' | 'dia' | 'sys' | 'mews'
  lineChartDataName: string
  recordedData: PatientRecordedDataType[] | undefined
  startTime: number
  endTime: number
  multiLineChartKey?: (
    | 'spo2'
    | 'pr'
    | 'resp'
    | 'temp'
    | 'dia'
    | 'sys'
    | 'mews'
  )[]
}

export default function RecordedDataChart({
  lineChartDataKey,
  lineChartDataName,
  recordedData,
  endTime,
  startTime,
  multiLineChartKey,
}: RecordedDataLineChartProp) {
  const chartData = useMemo(() => {
    return recordedData
      ? recordedData.map((data, index) => {
        let nextIndex = index + 1 < recordedData.length ? index + 1 : index
        if (lineChartDataKey) {
          let value = valueManipulation(
            `${data[lineChartDataKey]}`,
            lineChartDataKey
          )
          return {
            [`${lineChartDataKey}-${getScore(data, lineChartDataKey)}`]:
              value,
            [`${lineChartDataKey}-${getScore(recordedData[nextIndex], lineChartDataKey)}`]:
              value,
            [`${lineChartDataKey}`]: value,
            iscore: getScore(recordedData[nextIndex], lineChartDataKey),
            time: dayjs(data.createTimestamp).valueOf(),
          }
        } else if (multiLineChartKey) {
          let chartData: Record<string, string | number | undefined | null> =
            {}
          multiLineChartKey.forEach((key) => {
            let value = valueManipulation(`${data[key]}`, key)
            let obj = {
              [`${key}-${getScore(data, key)}`]: value,
              // [`${key}-${getScore(recordedData[nextIndex], key)}`]: value,
              [`${key}`]: value,
            }
            chartData = { ...chartData, ...obj }
          })
          return {
            ...chartData,
            time: dayjs(data.createTimestamp).valueOf(),
          }
        } else {
          return {
            time: dayjs(data.createTimestamp).valueOf(),
          }
        }
      })
      : []
  }, [lineChartDataKey, multiLineChartKey, recordedData])

  const yAxisDomain = useMemo(() => {
    if (lineChartDataKey) {
      if (lineChartDataKey === 'mews') {
        return [-1, 3]
      }

      if (lineChartDataKey === 'resp') {
        return ['dataMin - 25', 'dataMax + 25']
      }

      return ['dataMin - 2', 'dataMax + 2']
    }

    if (multiLineChartKey) {
      return ['dataMin - 25', 'dataMax + 25']
    }
  }, [lineChartDataKey, multiLineChartKey])

  const CustomTooltip = useCallback(
    ({ active, payload }: TooltipProps<ValueType, NameType>) => {
      if (active && payload && payload.length) {
        let time = dayjs(payload[0].payload.time).format('DD-MMM-YY HH:mm:ss')
        return (
          <ToolTipContainer>
            {multiLineChartKey ? (
              <TooltipInnerContainer>
                {payload.map((key, index) => {
                  return (
                    multiLineChartKey[index] &&
                    index < multiLineChartKey.length && (
                      <span>
                        <strong> {multiLineChartKey[index]} </strong>{' '}
                        {`${key.payload[`${multiLineChartKey[index]}`]}`}
                      </span>
                    )
                  )
                })}
                <span>
                  <strong> Time </strong> {time}
                </span>
              </TooltipInnerContainer>
            ) : (
              <TooltipInnerContainer>
                <span>
                  <strong> {lineChartDataKey} </strong>{' '}
                  {`${payload[0].payload[`${lineChartDataKey}`]}`}
                </span>
                <span>
                  <strong> Time </strong> {time}
                </span>
              </TooltipInnerContainer>
            )}
          </ToolTipContainer>
        )
      }
      return <></>
    },
    [lineChartDataKey, multiLineChartKey]
  )

  return (
    <ChartContainer>
      <ChartTitle>
        {getRecordedChartTitle(lineChartDataName, recordedData)}
      </ChartTitle>
      <ResponsiveContainer
        width='100%'
        height='100%'
        minWidth={0}
        minHeight={240}
        maxHeight={undefined}
        aspect={undefined}
      >
        <LineChart
          data={chartData}
          margin={{
            top: 8,
            right: 24,
            left: 24,
            bottom: 8,
          }}
        >
          <CartesianGrid strokeDasharray='3 3' />
          {chartData.length > 0 && (
            <XAxis
              fontSize={10}
              type='number'
              tickCount={7}
              tick={({ payload, x, y }) => {
                const date = dayjs(payload.value).format('DD-MMM')
                const time = dayjs(payload.value).format('HH:mm')
                return (
                  <g transform={`translate(${x},${y})`}>
                    <text x={0} y={0} dy={16} textAnchor='middle' fill='#666'>
                      <tspan x='0' dy='1em'>
                        {date}
                      </tspan>
                      <tspan x='0' dy='1.2em'>
                        {time}
                      </tspan>
                    </text>
                  </g>
                )
              }}
              dataKey='time'
              domain={[dayjs(startTime).valueOf(), dayjs(endTime).valueOf()]}
              interval={'preserveStartEnd'}
            />
          )}
          {chartData.length > 0 && (
            <YAxis dataKey={lineChartDataKey} domain={yAxisDomain} />
          )}
          <Tooltip content={<CustomTooltip />} cursor={chartData.length > 0} />
          {lineChartDataKey &&
            ['black', 'green', '#fff991', '#ffcc6c', '#ff6d6d'].map(
              (color, index) => {
                let score = index - 1
                return (
                  <Line
                    key={color}
                    isAnimationActive={false}
                    name={`${lineChartDataKey}`}
                    type='linear'
                    strokeWidth={3}
                    dataKey={`${lineChartDataKey}-${score}`}
                    stroke={color}
                    dot={false}
                    activeDot={{ r: 0 }}
                  />
                )
              }
            )}

          {multiLineChartKey &&
            multiLineChartKey.map((key) =>
              ['black', '#cc00dc', '#fff991', '#ffcc6c', '#ff6d6d'].map(
                (color, index) => {
                  let score = index - 1;
                  return (
                    <Line
                      isAnimationActive={false}
                      name={`${key}`}
                      type="linear"
                      strokeWidth={2}
                      dataKey={`${key}-${score}`}
                      stroke={key === 'dia' ? 'blue' : color}
                      dot={true}
                      activeDot={{ r: 8, stroke: 'white', strokeWidth: 2 }}
                    >
                    
                    </Line>
                  );
                }
              )
            )}
        </LineChart>
      </ResponsiveContainer>
    </ChartContainer>
  )
}

function findMinMax(
  arr: PatientRecordedDataType[],
  key: 'spo2' | 'pr' | 'resp' | 'temp' | 'dia' | 'sys' | 'mews'
) {
  if (arr.length === 0) {
    return null
  }

  let minValue = null
  let maxValue = null

  for (let i = 0; i < arr.length; i++) {
    const value = arr[i][key]

    if (value !== null) {
      minValue = value
      maxValue = value
      break
    }
  }

  for (let i = 0; i < arr.length; i++) {
    const value = arr[i][key]

    if (value !== null) {
      minValue = Math.min(minValue!, value)
      maxValue = Math.max(maxValue!, value)
    }
  }

  if (key === 'mews') {
    minValue = minValue && minValue < 0 ? 0 : minValue
    return { min: minValue, max: maxValue }
  }
  if (minValue && maxValue) {
    return { min: minValue, max: maxValue }
  } else {
    return null
  }
}

function getRecordedChartTitle(
  chartName: string,
  chartData: PatientRecordedDataType[] | undefined
) {
  if (!chartData) return ''
  if (chartName === 'mews') {
    let rangeValues = findMinMax(chartData, chartName)
    if (!rangeValues) return 'HS : NO DATA'
    return `HS MAX: ${rangeValues.max} ; MIN: ${rangeValues.min}`
  } else if (chartName === 'spo2') {
    let rangeValues = findMinMax(chartData, chartName)
    if (!rangeValues) return `SpO₂ : NO DATA`;
    return `SpO₂ MAX: ${rangeValues.max}% ; MIN: ${rangeValues.min}%`
  } else if (chartName === 'pr') {
    let rangeValues = findMinMax(chartData, chartName)
    if (!rangeValues) return 'PR : NO DATA'
    return `PR MAX: ${rangeValues.max} bpm ; MIN: ${rangeValues.min} bpm`
  } else if (chartName === 'temp') {
    let rangeValues = findMinMax(chartData, chartName)
    if (!rangeValues) return 'Temp : NO DATA'
    return `Temp MAX: ${rangeValues.max} °F ; MIN: ${rangeValues.min} °F`
  } else if (chartName === 'resp') {
    let rangeValues = findMinMax(chartData, chartName)
    if (!rangeValues) return 'Resp : NO DATA'
    return `Resp MAX: ${rangeValues.max} bpm ; MIN: ${rangeValues.min} bpm`
  } else if (chartName === 'bp') {
    let rangeValuesSys = findMinMax(chartData, 'sys')
    let rangeValuesDia = findMinMax(chartData, 'dia')
    if (!rangeValuesSys || !rangeValuesDia) return 'BP : NO DATA'
    return `BP MAX: ${rangeValuesSys.max}/${rangeValuesDia.max} ; MIN: ${rangeValuesSys.min}/${rangeValuesDia.min} `
  }
  return ''
}

function parseNumber(value: string) {
  if (!value || isNaN(parseInt(value))) return 0
  return parseInt(value)
}

const getScore = (
  data: PatientRecordedDataType,
  key: 'spo2' | 'pr' | 'resp' | 'temp' | 'dia' | 'sys' | 'mews'
) => {
  let score = 0

  if (key === 'dia' || key === 'sys') {
    score = parseNumber(`${data[`bpScore`]}`)
  } else if (key === 'mews') {
    score = parseNumber(`${data[`mews`]}`)
  } else {
    score = parseNumber(`${data[`${key}Score`]}`)
  }

  return score >= 3 ? 3 : score
}

function valueManipulation(
  value: string,
  key: 'spo2' | 'pr' | 'resp' | 'temp' | 'dia' | 'sys' | 'mews'
) {
  let numValue = key === 'temp' ? parseFloat(value) : parseInt(value)
  if (key === 'mews') return numValue < 0 ? null : numValue
  if (!numValue) return undefined
  return numValue
}
